import { KeyboardEvent, useState } from "react";
import {
  ClickAwayListener,
  IconButton,
  Paper,
  MenuList,
  Popper,
  styled,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";

import { useTranslation } from "react-i18next";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { InputBase } from "formik-mui";

import { SelectedLocationState, LocationResult, SearchForm } from "../../types";
import { LocationDebouncedSubmit } from "./LocationDebouncedSubmit";
import { LocationSearchResults } from "./LocationSearchResults";
import { useMap } from "react-leaflet";

interface Props {
  type: string;
  onClose: () => void;
}

export function LocationSearch(props: Props) {
  const { type, onClose } = props;
  const { t } = useTranslation();
  const map = useMap();
  const [elevation, setElevation] = useState(0);
  const [selected, setSelected] = useState<SelectedLocationState>({
    numItems: 0,
    selectedIndex: 0,
    selectedItem: null,
  });
  const [searchValue, setSearchValue] = useState(
    selected.selectedItem?.displayName || ""
  );
  const [anchorRef, setAnchorRef] = useState<HTMLDivElement | null>(null);

  const initialValues = { search: "" };

  async function onSubmit(
    values: SearchForm,
    helpers: FormikHelpers<SearchForm>
  ) {
    const { setSubmitting } = helpers;
    setSubmitting(true);
    setSearchValue(values.search.trim());
    setTimeout(setSubmitting, 1000, false);
  }

  function onSelect(result: LocationResult) {
    map.fitBounds(result.boundingbox);
    setSelected({ ...selected, selectedItem: result });
  }

  function onKey(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case "PageUp":
      case "PageDown":
        event.preventDefault();
        event.stopPropagation();
        setSelected({
          ...selected,
          selectedIndex: getNextIndex(
            event.key,
            selected.selectedIndex,
            selected.numItems
          ),
        });
    }
  }

  function getNextIndex(key: string, current: number, max: number) {
    switch (key) {
      case "ArrowUp":
        if (current > 0) return --current;
        return max - 1;
      case "ArrowDown":
        if (current < max - 1) return ++current;
        return 0;
      case "PageUp":
        return 0;
      case "PageDown":
        return max - 1;
    }

    return 0;
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        onClose();
      }}
    >
      <Styles elevation={elevation} ref={(ref) => setAnchorRef(ref)}>
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
          {({ submitForm, values, dirty, setFieldValue }) => (
            <Form className="disable-helpertext-margin">
              <LocationDebouncedSubmit
                submitForm={submitForm}
                values={values}
                dirty={dirty}
              />
              <Field
                component={InputBase}
                name="search"
                id="search-field"
                placeholder={t("label.searchALocation")}
                autoFocus
                onFocus={() => {
                  setElevation(4);
                  setSelected({ ...selected, selectedItem: null });
                }}
                disabled={false}
                onBlur={() => setElevation(0)}
                onKeyDown={onKey}
                autoComplete="off"
              />
              {!selected.selectedItem && !values.search && (
                <IconButton type="submit">
                  <Search />
                </IconButton>
              )}
              {(selected.selectedItem !== null || values.search) && (
                <IconButton
                  onClick={() => {
                    setFieldValue("search", "", false);
                    setSearchValue("");
                    setSelected({ ...selected, selectedItem: null });
                  }}
                >
                  <Close />
                </IconButton>
              )}
              {searchValue && anchorRef && !selected.selectedItem && (
                <Popper
                  anchorEl={anchorRef}
                  open={searchValue !== ""}
                  placement="bottom"
                  disablePortal
                >
                  <Paper>
                    <MenuList
                      autoFocusItem={searchValue !== ""}
                      variant="menu"
                      sx={{
                        minWidth: `${anchorRef.clientWidth}px`,
                        maxWidth: `${anchorRef.clientWidth}px`,
                      }}
                    >
                      <LocationSearchResults
                        type={type}
                        query={searchValue}
                        selected={selected}
                        setSelected={setSelected}
                        onSelect={(result) => {
                          onSelect(result);
                          setSearchValue("");
                          setFieldValue("search", result.displayName, false);
                          onClose();
                        }}
                      />
                    </MenuList>
                  </Paper>
                </Popper>
              )}
            </Form>
          )}
        </Formik>
      </Styles>
    </ClickAwayListener>
  );
}

const Styles = styled(Paper)`
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  display: flex;
  align-content: center;

  form {
    display: flex;
    flex-wrap: nowrap;
  }

  .MuiInputBase-root {
    color: ${({ theme }) => theme.palette.common.white};
    margin-right: ${({ theme }) => theme.spacing(1)};
  }
`;
